import React from 'react';

import { st, classes } from './Widget.st.css';

import { EmptyList } from '../components/EmptyList';

import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { IChallengeListStateProps } from './interfaces';
import { Spinner } from '../../../components-shared/Spinner';
import { Title } from '../components/Title';
import {
  Tabs,
  TabsAlignment as TabsALIGNMENT,
  TabsSkin as TabsSKIN,
  TPAComponentsProvider,
} from '@wix/wix-ui-tpa/cssVars';
import { MemberChallenge } from '@wix/ambassador-challenges-v1-challenge/types';
import { State as ParticipantState } from '@wix/ambassador-challenges-v1-participant/types';
import { ChallengeItem } from '../components/ChallengeItem';
import { LeaveChallengeModalProvider } from '../../../contexts/LeaveChallengeModal/LeaveChallengeModalProvider';
import { useSettings } from '@wix/tpa-settings/react';
import settingsParams from '../settingsParams';
import { useUser } from '../../../contexts/User/UserContext';

// const ACTIVE_CHALLENGES_TAB = 0;
const ENDED_CHALLENGES_TAB = 1;
// Stable ids: wix-ui-tpa Tabs sets aria-controls="${id}-tab" on each tab
const TAB_IDS = ['members-area-tab-active', 'members-area-tab-ended'];

const isEndedChallenge = (challenge: MemberChallenge): boolean => {
  return [ParticipantState.COMPLETED, ParticipantState.FAILED].includes(
    challenge.summary?.participation?.state as any,
  );
};

export default (props: IChallengeListStateProps) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [tab, setTab] = React.useState<number>(0);
  const { t } = useTranslation();
  const { isMobile, isRTL } = useEnvironment();
  const { leaveProgram } = useUser();
  const settings = useSettings();
  const { challengesListData } = props;

  const endedChallenges =
    challengesListData?.memberChallenges?.filter(isEndedChallenge) || [];
  const activeChallenges =
    challengesListData?.memberChallenges?.filter((challenge) => {
      return !isEndedChallenge(challenge);
    }) || [];

  const [items, setItems] = React.useState<MemberChallenge[]>(activeChallenges);

  const getTabsAlignment = React.useMemo(() => {
    if (isMobile) {
      return TabsALIGNMENT.center;
    }

    if (isRTL) {
      return TabsALIGNMENT.right;
    }

    return TabsALIGNMENT.left;
  }, [isMobile, isRTL]);

  React.useEffect(() => {
    setItems(tab === ENDED_CHALLENGES_TAB ? endedChallenges : activeChallenges);
  }, [tab]);

  const removeChallenge = (challengeId: string) => {
    setItems(items.filter((item) => item.challenge.id !== challengeId));
  };

  const toggleIsLoading = () => {
    setIsLoading(!isLoading);
  };

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <Spinner />
      </div>
    );
  }

  return (
    <TPAComponentsProvider value={{ mobile: isMobile, rtl: isRTL }}>
      <LeaveChallengeModalProvider leaveProgram={leaveProgram} showModal={null}>
        <div
          data-hook="my-challenge-list-wrapper"
          className={st(classes.root, { mobile: isMobile })}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {!isMobile && <Title />}

          <Tabs
            className={classes.tabs}
            skin={TabsSKIN.fullUnderline}
            alignment={getTabsAlignment}
            items={[
              {
                id: TAB_IDS[0],
                title: t('members-area.tab.active'),
              },
              {
                id: TAB_IDS[1],
                title: t('members-area.tab.ended'),
              },
            ]}
            onTabClick={setTab}
            activeTabIndex={tab}
            isTablist={true}
          />

          {TAB_IDS.map((tabId, tabIndex) => (
            <div
              key={tabId}
              role="tabpanel"
              id={`${tabId}-tab`}
              aria-labelledby={tabId}
              hidden={tabIndex !== tab}
            >
              {tabIndex === tab ? (
                <>
                  {!items?.length || props.showMAEmptyList ? (
                    <EmptyList
                      title={
                        settings.get(
                          settingsParams.emptyChallengesText,
                        ) as string
                      }
                      onLinkClick={toggleIsLoading}
                    />
                  ) : (
                    <ul data-hook="challenge-list" className={classes.wrapper}>
                      {items.map((item, index) => {
                        return (
                          <li className={classes.wrapperItem} key={index}>
                            <ChallengeItem
                              item={item}
                              onChallengeRemove={removeChallenge}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              ) : null}
            </div>
          ))}
        </div>
      </LeaveChallengeModalProvider>
    </TPAComponentsProvider>
  );
};
